
import React, { useState } from 'react';
import { ShieldCheck, ArrowLeft, CheckCircle, AlertTriangle, UserCheck, Globe, Zap, Info, Copy, Check, Share2, MessageCircle, Send, Instagram, Facebook, Twitter, MessageSquare, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MAIN_COUPON_CODE, WHATSAPP_CHANNEL_LINK, TELEGRAM_LINK, INSTAGRAM_LINK } from '../constants';

const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const SafetyAndTransparency: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(MAIN_COUPON_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const faqData = [
    {
      q: "IS THIS THE OFFICIAL PHYSICS WALLAH WEBSITE?",
      a: "No. The only official website is pw.live. We are an authorized ambassador resource site designed to help students find working discounts."
    },
    {
      q: "WHY DO YOU PROVIDE THESE CODES FOR FREE?",
      a: "To help the student community and support our platform through the official PW ambassador program."
    },
    {
        q: "CAN I USE THIS CODE FOR OFFLINE VIDYAPEETH CENTERS?",
        a: "Yes! Coupon code JITJAN0001 is applicable for Vidyapeeth offline centers too. You can provide it at the admission desk or use it while registering for Vidyapeeth via the official app."
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-white mb-12 transition-colors font-black uppercase text-[10px] tracking-widest">
        <ArrowLeft size={16} /> Back to Hub
      </Link>

      <div className="text-center mb-20">
        <div className="inline-block bg-green-500/10 border border-green-500/30 text-green-500 px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-8 animate-pulse">
          Verified Transparency Report
        </div>
        <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter italic leading-none mb-10">
          SAFE, GENUINE & <br />
          <span className="text-blue-500 not-italic">TRANSPARENT</span>
        </h1>
        <p className="text-xl text-gray-500 font-bold uppercase tracking-tight italic">
          Explaining how we provide verified PW discounts without <br /> compromising your security.
        </p>
      </div>

      <div className="prose prose-invert max-w-none text-gray-400 mb-20">
        <p className="text-lg leading-relaxed font-medium mb-8">
          In an era of internet scams and "clickbait" offers, it is natural for a student to wonder: 
          <span className="text-white"> "Is this PW coupon website safe?"</span> or 
          <span className="text-white"> "Is that 20% discount code actually real?"</span>
        </p>
        <p className="text-lg leading-relaxed font-medium">
          At PW Coupon Hub, we believe that trust is the foundation of education. This page is dedicated to providing you with full transparency on how our platform works, how we make money, and why you can safely use our verified codes.
        </p>
      </div>

      {/* Security Commitment Box */}
      <div className="bg-white/5 border border-white/10 rounded-[48px] p-8 md:p-12 mb-20 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 transition-opacity">
           <ShieldCheck size={160} />
        </div>
        <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-12 italic relative z-10 underline decoration-blue-600 decoration-4">OUR SECURITY COMMITMENT</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
          <div className="flex gap-4">
             <div className="mt-1 bg-green-500/20 p-2 rounded-xl h-fit">
                <UserCheck className="text-green-500" size={24} />
             </div>
             <div>
                <h4 className="text-white font-black uppercase tracking-widest mb-2">NO PERSONAL DATA</h4>
                <p className="text-xs text-gray-500 font-bold leading-relaxed">
                  We never ask for your PW login, password, or phone number. All transactions happen on the official pw.live app.
                </p>
             </div>
          </div>
          <div className="flex gap-4">
             <div className="mt-1 bg-green-500/20 p-2 rounded-xl h-fit">
                <Globe className="text-green-500" size={24} />
             </div>
             <div>
                <h4 className="text-white font-black uppercase tracking-widest mb-2">NO MALWARE / SPAM</h4>
                <p className="text-xs text-gray-500 font-bold leading-relaxed">
                  This site is a static educational resource. We don't use intrusive pop-ups, tracking cookies, or shady downloads.
                </p>
             </div>
          </div>
        </div>
      </div>

      {/* Section 1 */}
      <div className="mb-20">
        <h3 className="text-4xl font-black text-white uppercase tracking-tighter mb-8 italic">1. HOW OUR COUPON LINKS WORK</h3>
        <p className="text-gray-400 font-medium mb-10 leading-relaxed">
          We are part of the Physics Wallah Student Ambassador Program. This is an official initiative by PW to reach more students through community referrals.
        </p>
        <div className="space-y-6">
           <div className="flex gap-4 items-start border-l-4 border-blue-600 pl-6 py-2">
              <span className="text-white font-black uppercase text-sm tracking-widest shrink-0 mt-1">• The Code:</span>
              <p className="text-gray-500 font-medium">When you enter code <strong className="text-blue-500">{MAIN_COUPON_CODE}</strong> on the PW app or at Vidyapeeth centers, the system recognizes us as your referring ambassador.</p>
           </div>
           <div className="flex gap-4 items-start border-l-4 border-blue-600 pl-6 py-2">
              <span className="text-white font-black uppercase text-sm tracking-widest shrink-0 mt-1">• Your Benefit:</span>
              <p className="text-gray-500 font-medium">You get an instant discount (the maximum allowed by the PW system) on your batch fee, whether online or offline.</p>
           </div>
           <div className="flex gap-4 items-start border-l-4 border-blue-600 pl-6 py-2">
              <span className="text-white font-black uppercase text-sm tracking-widest shrink-0 mt-1">• Our Benefit:</span>
              <p className="text-gray-500 font-medium">We may receive a small referral commission from Physics Wallah for helping a new student join their ecosystem. This helps us keep this resource-free and updated.</p>
           </div>
        </div>
        <div className="mt-10 bg-yellow-500/10 border border-yellow-500/20 p-6 rounded-3xl">
           <p className="text-[11px] text-gray-400 font-bold uppercase italic leading-relaxed">
             <span className="text-yellow-500 font-black not-italic">Important:</span> Using our code never increases your price. It only decreases it or stays the same if you already have the lowest price.
           </p>
        </div>
      </div>

      {/* Section 2 */}
      <div className="mb-20">
        <h3 className="text-4xl font-black text-white uppercase tracking-tighter mb-8 italic">2. HOW TO SPOT "FAKE" COUPON SITES</h3>
        <p className="text-gray-400 font-medium mb-12">Many websites use deceptive tactics to steal your time or data. Watch out for these red flags:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
           <div className="bg-red-500/5 border border-red-500/10 p-8 rounded-[40px] hover:bg-red-500/10 transition-colors group">
              <h4 className="text-red-500 font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                <AlertTriangle size={18} /> FLAG #1: IMPOSSIBLE DISCOUNTS
              </h4>
              <p className="text-xs text-gray-500 font-bold leading-relaxed group-hover:text-gray-400 transition-colors">
                If a site promises "90% OFF" or "Get Yakeen Batch for ₹10," it is a lie. PW discounts are usually between ₹200 to ₹1,000 depending on the season.
              </p>
           </div>
           <div className="bg-red-500/5 border border-red-500/10 p-8 rounded-[40px] hover:bg-red-500/10 transition-colors group">
              <h4 className="text-red-500 font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                <Info size={18} /> FLAG #2: HIDDEN CONTENT
              </h4>
              <p className="text-xs text-gray-500 font-bold leading-relaxed group-hover:text-gray-400 transition-colors">
                Sites that force you to "Click to Reveal" and then open 10 random advertisements are just farming your clicks. Our codes are always visible and copyable.
              </p>
           </div>
        </div>
      </div>

      {/* Student-First Promise Section (From Image) */}
      <div className="bg-[#111] border border-white/5 rounded-[60px] p-12 md:p-20 mb-20 relative overflow-hidden group">
        <div className="absolute inset-0 bg-blue-600/5 blur-[100px] group-hover:scale-150 transition-transform duration-1000"></div>
        <div className="relative z-10">
          <p className="text-gray-500 font-black uppercase tracking-widest text-xs leading-relaxed mb-12 max-w-2xl mx-auto text-center">
            We are former aspirants ourselves. We know that every rupee saved is a rupee that can be spent on books or test series. We promise to:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             <div className="p-8 glass-morphism rounded-[32px] border border-white/10 text-center flex flex-col items-center">
                <h4 className="text-yellow-500 font-black uppercase text-sm mb-3">VERIFY DAILY</h4>
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">WE TEST EVERY CODE ON THE PW APP EVERY 24 HOURS.</p>
             </div>
             <div className="p-8 glass-morphism rounded-[32px] border border-white/10 text-center flex flex-col items-center">
                <h4 className="text-yellow-500 font-black uppercase text-sm mb-3">NO MISLEADING</h4>
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">IF A BATCH HAS NO ACTIVE COUPONS, WE TELL YOU CLEARLY.</p>
             </div>
             <div className="p-8 glass-morphism rounded-[32px] border border-white/10 text-center flex flex-col items-center">
                <h4 className="text-yellow-500 font-black uppercase text-sm mb-3">SUPPORT ALWAYS</h4>
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">HUMAN HELP IS AVAILABLE VIA WHATSAPP OR TELEGRAM IF YOU FACE AN ERROR.</p>
             </div>
          </div>
        </div>
      </div>

      {/* Social Spread Section */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-20 px-8">
         <div className="text-left">
            <h4 className="text-white font-black uppercase text-xs tracking-[0.2em] mb-1">SPREAD THE KNOWLEDGE</h4>
            <p className="text-gray-600 text-[10px] font-bold uppercase tracking-widest">Help your fellow aspirants save today.</p>
         </div>
         <div className="flex gap-4">
            <a href={WHATSAPP_CHANNEL_LINK} target="_blank" className="p-3 bg-white/5 rounded-full hover:bg-[#25D366] transition-all text-gray-500 hover:text-white"><WhatsAppIcon size={18} /></a>
            <a href={TELEGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-full hover:bg-blue-500 transition-all text-gray-500 hover:text-white"><Send size={18} /></a>
            <a href={INSTAGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-full hover:bg-pink-600 transition-all text-gray-500 hover:text-white"><Instagram size={18} /></a>
            <a href="#" className="p-3 bg-white/5 rounded-full hover:bg-white/10 transition-all text-gray-500 hover:text-white"><Twitter size={18} /></a>
         </div>
      </div>

      {/* Unlock Discount Section (From Image) */}
      <div className="bg-[#1a1a1a] rounded-[60px] p-12 md:p-24 text-center mb-20 shadow-[0_40px_100px_rgba(0,0,0,0.8)] border border-white/5 relative group overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 to-transparent opacity-30"></div>
        <div className="relative z-10 flex flex-col items-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-[10px] font-black uppercase tracking-[0.3em] mb-10">
            <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></span>
            OFFICIAL AMBASSADOR OFFER 2026
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter mb-8 leading-none italic">
            UNLOCK YOUR <span className="text-yellow-500">SPECIAL</span> <br /> DISCOUNT
          </h2>
          
          <p className="text-gray-500 font-bold text-sm max-w-lg mx-auto mb-16 leading-relaxed text-center">
            Don't pay full price for your future. Use the code <strong className="text-white font-black underline decoration-yellow-500">{MAIN_COUPON_CODE}</strong> <br /> for both Online Batches and Offline Vidyapeeth centers.
          </p>
          
          <div className="w-full max-w-md bg-white/5 rounded-[40px] p-8 md:p-12 border border-white/10 backdrop-blur-3xl shadow-3xl mb-12">
             <p className="text-[10px] text-gray-600 font-black uppercase tracking-[0.4em] mb-6">CURRENT ACTIVE CODE</p>
             <h3 className="text-4xl md:text-6xl font-mono font-black text-yellow-500 tracking-tighter mb-10">{MAIN_COUPON_CODE}</h3>
             <button 
                onClick={handleCopy}
                className="w-full py-5 bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-[0.4em] text-xs rounded-2xl transition-all active:scale-95 border border-white/10 flex items-center justify-center gap-3"
             >
                {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                {copied ? 'COPIED' : 'COPY'}
             </button>
             
             <div className="mt-12 pt-8 border-t border-white/5">
                <p className="text-[10px] text-gray-700 font-black uppercase tracking-[0.3em] mb-6">SHARE WITH FELLOW ASPIRANTS</p>
                <div className="flex justify-center gap-4">
                  <a href={WHATSAPP_CHANNEL_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl text-gray-500 hover:text-white hover:bg-[#25D366] transition-all"><WhatsAppIcon size={18} /></a>
                  <a href={TELEGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl text-gray-500 hover:text-white hover:bg-blue-500 transition-all"><Send size={18} /></a>
                  <a href={INSTAGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl text-gray-500 hover:text-white hover:bg-pink-600 transition-all"><Instagram size={18} /></a>
                  <a href="#" className="p-3 bg-white/5 rounded-2xl text-gray-500 hover:text-white hover:bg-white/10 transition-all"><Twitter size={18} /></a>
                </div>
             </div>
          </div>

          <div className="grid grid-cols-3 gap-12 w-full max-w-md">
             <div className="text-center">
                <p className="text-[8px] text-gray-600 font-black uppercase tracking-widest mb-1">STATUS</p>
                <p className="text-[10px] text-green-500 font-black uppercase tracking-widest">ACTIVE</p>
             </div>
             <div className="text-center">
                <p className="text-[8px] text-gray-600 font-black uppercase tracking-widest mb-1">REBATE</p>
                <p className="text-[10px] text-yellow-500 font-black uppercase tracking-widest">MAXIMUM</p>
             </div>
             <div className="text-center">
                <p className="text-[8px] text-gray-600 font-black uppercase tracking-widest mb-1">USABLE</p>
                <p className="text-[10px] text-white font-black uppercase tracking-widest">UNIVERSAL</p>
             </div>
          </div>
        </div>
      </div>

      {/* Transparency FAQ Section (From Image) */}
      <div className="bg-white/5 rounded-[60px] p-12 md:p-24 border border-white/10 backdrop-blur-xl">
        <h3 className="text-3xl font-black text-white uppercase tracking-tighter mb-12 italic border-l-4 border-blue-600 pl-6">TRANSPARENCY FAQ</h3>
        <div className="space-y-12">
           {faqData.map((faq, i) => (
             <div key={i} className="group">
                <h4 className="text-white font-black uppercase text-sm tracking-widest mb-4 flex items-center gap-3">
                   <HelpCircle size={18} className="text-blue-500" /> Q: {faq.q}
                </h4>
                <p className="text-gray-500 font-medium leading-relaxed pl-8 group-hover:text-gray-400 transition-colors">
                   A: {faq.a}
                </p>
             </div>
           ))}
        </div>
      </div>

      <div className="mt-20 pt-10 border-t border-white/5 text-center">
         <Link to="/legal-disclaimer" className="text-gray-700 hover:text-white font-black uppercase text-[10px] tracking-[0.4em] transition-colors">
            Read Detailed Legal Disclaimer
         </Link>
      </div>
    </div>
  );
};

export default SafetyAndTransparency;
